import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Clock3, Send, Loader2, Siren } from "lucide-react";
import { Reveal, MaskedLine, Overline } from "@/components/Reveal";
import { CONTACT, PRODUCTS, SERVICES } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: "data-engineering", label: "Data Engineering & Analytics" },
  { id: "cloud-migration", label: "Cloud Migration (Snowflake / Databricks)" },
  { id: "sas-onprem", label: "SAS & On-Prem Databases" },
  { id: "networking", label: "Enterprise Networking" },
  { id: "ai-agentic", label: "AI & Agentic AI Solutions" },
  { id: "hardware", label: "Hardware Procurement" },
  { id: "support", label: "Support / AMC" },
  { id: "other", label: "Something else" },
];

const InfoCard = ({ icon: Icon, title, children, testid }) => (
  <div className="rounded-3xl bg-white border border-gray-200 p-6" data-testid={testid}>
    <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-cobalt-light">
      <Icon className="h-5 w-5 text-cobalt" />
    </div>
    <h3 className="mt-4 font-display font-bold text-ink text-sm">{title}</h3>
    <div className="mt-2 text-sm text-gray-600 leading-relaxed">{children}</div>
  </div>
);

export default function Contact() {
  const [params] = useSearchParams();
  const productParam = params.get("product");
  const serviceParam = params.get("service");

  const initialCategory = params.get("category")
    || (serviceParam && CATEGORIES.some((c) => c.id === serviceParam) ? serviceParam : null)
    || (productParam ? "hardware" : null)
    || "cloud-migration";

  const initialMessage = productParam
    ? `We would like a bulk quote for ${PRODUCTS.find((p) => p.id === productParam)?.name || "hardware"}. `
    : serviceParam
      ? `We would like to discuss ${SERVICES.find((s) => s.id === serviceParam)?.title || "your services"}. `
      : "";

  const [form, setForm] = useState({ name: "", email: "", phone: "", category: initialCategory, message: initialMessage });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        category: form.category,
        message: form.message.trim(),
      });
      toast.success("Enquiry sent — we will respond within one business day.");
      setForm({ name: "", email: "", phone: "", category: form.category, message: "" });
    } catch (err) {
      toast.error("Could not send your enquiry. Please email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="blueprint-grid" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 pb-14">
          <Overline>Contact</Overline>
          <h1 className="mt-4 font-display font-extrabold tracking-tight leading-[1.04] text-4xl sm:text-5xl lg:text-6xl text-ink max-w-3xl">
            <MaskedLine delay={0.15}>Tell us what you're</MaskedLine>
            <MaskedLine delay={0.3}>
              <span className="text-cobalt italic">modernising.</span>
            </MaskedLine>
          </h1>
          <Reveal delay={0.45}>
            <p className="mt-6 text-base sm:text-lg text-gray-600 max-w-2xl leading-relaxed">
              Migration assessment, networking rollout or a hardware quote — one form, one business day response.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-20 sm:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-7">
            <form onSubmit={submit} className="rounded-3xl bg-white border border-gray-200 p-7 sm:p-9 shadow-sm" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="contact-name" className="font-mono text-xs uppercase tracking-[0.18em] text-gray-500 block mb-2">Name *</label>
                  <input
                    id="contact-name"
                    data-testid="contact-input-name"
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Your full name"
                    className="w-full rounded-xl border border-gray-200 bg-paper px-4 py-3 text-sm text-ink outline-none focus:border-cobalt focus:ring-2 focus:ring-cobalt/15 transition"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="font-mono text-xs uppercase tracking-[0.18em] text-gray-500 block mb-2">Email *</label>
                  <input
                    id="contact-email"
                    type="email"
                    data-testid="contact-input-email"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="you@company.com"
                    className="w-full rounded-xl border border-gray-200 bg-paper px-4 py-3 text-sm text-ink outline-none focus:border-cobalt focus:ring-2 focus:ring-cobalt/15 transition"
                  />
                </div>
                <div>
                  <label htmlFor="contact-phone" className="font-mono text-xs uppercase tracking-[0.18em] text-gray-500 block mb-2">Phone</label>
                  <input
                    id="contact-phone"
                    data-testid="contact-input-phone"
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="+91-XXXXXXXXXX"
                    className="w-full rounded-xl border border-gray-200 bg-paper px-4 py-3 text-sm text-ink outline-none focus:border-cobalt focus:ring-2 focus:ring-cobalt/15 transition"
                  />
                </div>
                <div>
                  <label htmlFor="contact-category" className="font-mono text-xs uppercase tracking-[0.18em] text-gray-500 block mb-2">Requirement</label>
                  <select
                    id="contact-category"
                    data-testid="contact-select-category"
                    value={form.category}
                    onChange={set("category")}
                    className="w-full rounded-xl border border-gray-200 bg-paper px-4 py-3 text-sm text-ink outline-none focus:border-cobalt focus:ring-2 focus:ring-cobalt/15 transition"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c.id} value={c.id}>{c.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="mt-5">
                <label htmlFor="contact-message" className="font-mono text-xs uppercase tracking-[0.18em] text-gray-500 block mb-2">Message *</label>
                <textarea
                  id="contact-message"
                  rows={6}
                  data-testid="contact-input-message"
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Tell us about your current setup, volumes and timelines…"
                  className="w-full rounded-xl border border-gray-200 bg-paper px-4 py-3 text-sm text-ink outline-none focus:border-cobalt focus:ring-2 focus:ring-cobalt/15 transition resize-y"
                />
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-form-submit-btn"
                className="mt-7 inline-flex items-center gap-2 bg-cobalt hover:bg-cobalt-hover disabled:opacity-60 text-white font-semibold text-sm px-8 py-3.5 rounded-full transition-colors duration-300"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {sending ? "Sending…" : "Send enquiry"}
              </button>
            </form>
          </Reveal>

          <div className="lg:col-span-5 space-y-5">
            <Reveal delay={0.1}>
              <InfoCard icon={Mail} title="Email us" testid="contact-info-email">
                <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link" className="text-cobalt hover:text-cobalt-hover font-medium break-all">
                  {CONTACT.email}
                </a>
              </InfoCard>
            </Reveal>
            <Reveal delay={0.16}>
              <InfoCard icon={Phone} title="Call us" testid="contact-info-phone">
                <div className="space-y-1.5">
                  {CONTACT.phones.map((p, i) => (
                    <a key={p} href={CONTACT.phoneLinks[i]} data-testid={`contact-phone-link-${i}`} className="block text-cobalt hover:text-cobalt-hover font-medium">
                      {p}
                    </a>
                  ))}
                </div>
              </InfoCard>
            </Reveal>
            <Reveal delay={0.22}>
              <InfoCard icon={MapPin} title="Visit us" testid="contact-info-address">
                {CONTACT.address}
              </InfoCard>
            </Reveal>
            <Reveal delay={0.28}>
              <InfoCard icon={Clock3} title="Working hours" testid="contact-info-hours">
                {CONTACT.hours}
              </InfoCard>
            </Reveal>
            <Reveal delay={0.34}>
              <div className="rounded-3xl bg-ink text-white p-6 relative grain overflow-hidden" data-testid="contact-emergency-card">
                <div className="blueprint-grid-dark absolute inset-0 pointer-events-none" />
                <div className="relative">
                  <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-cobalt">
                    <Siren className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="mt-4 font-display font-bold text-sm">Support desk</h3>
                  <p className="mt-2 text-sm text-white/60 leading-relaxed">
                    Production pipeline down or hardware emergency? Call either number above — AMC clients get priority response.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
